/*
 * (c) 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 * 
 * This AWS Content is provided subject to the terms of the AWS Customer Agreement available at http://aws.amazon.com/agreement or other written agreement between Customer and either Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
 */

import React from "react";
import Game from "./Game"
import {Flashbar, Grid, GridProps, Spinner, Box, SpaceBetween} from "@cloudscape-design/components";

export interface GameData {
    sgId: string,
    appId: string,
    name: string,
    description: string,
    streamClass: string,
    regionStreamMap: { [key: string]: number };
    priority?: number;
}

interface GameListProps {
    region: string,
    games: GameData[],
    loading: boolean,
    loadError: string,
    onRefresh: () => void,
}

interface AuthContextProps
{
    auth:any
}

interface GameListState {
    error: string,
    success: string,
    activeStreamSgId: string | null,
    reconnectSgId: string | null,
}

class GameList extends React.Component<GameListProps & AuthContextProps, GameListState> {
    constructor(props: GameListProps & AuthContextProps) {
        super(props);
        this.state = {
            error: "",
            success:"",
            activeStreamSgId: null,
            reconnectSgId: null,
        };

        this.setError = this.setError.bind(this);
        this.setSuccess = this.setSuccess.bind(this);
        this.setActiveStream = this.setActiveStream.bind(this);
        this.onReconnectAvailable = this.onReconnectAvailable.bind(this);
    }

    setError(error: string) {
        console.log("Setting error", error)
        this.setState({error: error})
    }

    setSuccess(success: string)
    {
        console.log("Setting success", success)
        this.setState({success: success})
    }

    setActiveStream(sgId: string | null) {
        this.setState({activeStreamSgId: sgId});
    }

    onReconnectAvailable(sgId: string | null) {
        this.setState({reconnectSgId: sgId});
    }

    render() {
        const hasRecreatingStreams = this.props.games.some(g => 
            g.sgId === 'RECREATING'
        );
        
        const filteredGames = this.props.games
            .filter(g => g.regionStreamMap?.hasOwnProperty(this.props.region))
            .sort((a, b) => {
                // Reconnect game always comes first
                if (a.sgId === this.state.reconnectSgId) return -1;
                if (b.sgId === this.state.reconnectSgId) return 1;
                // Then sort by priority (higher = preferred)
                return (b.priority ?? 0) - (a.priority ?? 0);
            });

        const gamelist = filteredGames.map((g, index) =>
            <Game key={g.appId + g.sgId} gameName={g.name} gameDescription={g.description} appId={g.appId}
                  sgId={g.sgId} streamClass={g.streamClass} region={this.props.region}
                  setError={this.setError} setSuccess={this.setSuccess}
                  auth={this.props.auth} remainingStreams={g.regionStreamMap[this.props.region]}
                  refreshList={this.props.onRefresh}
                  isRecommended={index === 0 && filteredGames.length > 1 && this.state.reconnectSgId === null}
                  isActiveStream={this.state.activeStreamSgId === g.sgId}
                  hasActiveStream={this.state.activeStreamSgId !== null}
                  setActiveStream={this.setActiveStream}
                  onReconnectAvailable={this.onReconnectAvailable}/>
        )

        const errorMessage = this.props.loadError || this.state.error;

        return (
            <div>
                <SpaceBetween size="m">
                    {hasRecreatingStreams ? <Flashbar items={[{
                        header: "Maintenance in Progress",
                        type: "warning",
                        content: "The streaming service is currently down for maintenance. Please check back later.",
                        dismissible: false,
                    }]}/> : null}
                    {errorMessage !== "" ? <Flashbar items={[{
                        header: "Oh no! A wild error occurred!",
                        type: "error",
                        content: errorMessage,
                        dismissible: true,
                        dismissLabel: "Dismiss message",
                        onDismiss: () => {
                            this.setState({error: ""})
                        },
                    }]}/> : null}
                    {this.state.success !== "" ? <Flashbar items={[{
                        header: "Success",
                        type: "success",
                        content: this.state.success,
                        dismissible: true,
                        dismissLabel: "Dismiss message",
                        onDismiss: () => {
                            this.setState({success: ""})
                        },
                    }]}/> : null}
                    <Grid gridDefinition={gamelist.map(e => {
                        return {colspan: {default: 12}} as GridProps.ElementDefinition
                    })}>
                    </Grid>
                    {this.props.loading ? (
                        <Box
                            textAlign="center"
                            padding={{ top: 'xxxl' }}
                        >
                            <Spinner size="large" variant="inverted" />
                        </Box>
                    ) : gamelist.length > 0 ? gamelist : (
                        <Flashbar items={[{
                            header: "No Streams Available",
                            type: "info",
                            content: "There are no streaming applications available in the selected region. Please try a different region.",
                            dismissible: false,
                        }]}/>
                    )}
                </SpaceBetween>
            </div>
        )
    }
}

export default GameList;
